import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { messagesAPI } from '~/api';
import {
  type Dictionary,
  MessageSource,
  type PaginationOptions,
  type TMessage,
} from '~/types';
import { type RootState } from '~/store';

export type GetAllParams = {
  chatID: string;
  options?: PaginationOptions;
};

const getAll = createAsyncThunk(
  'message/getAll',
  async ({ chatID, options }: GetAllParams) => {
    const { items } = await messagesAPI.getAll(chatID, options);
    return { chatID, messages: items };
  },
);

const getInitialChatState = (): ChatMessages => ({
  map: {},
  items: [],
  toolCallMap: {},
  page: 1,
  loading: false,
  isEnd: false,
  isAssistantResponding: false,
  currentToolCalls: [],
});

const addMessages = (
  state: MessageState,
  action: PayloadAction<{ chatID: string; messages: TMessage[] }>,
) => {
  const { chatID, messages } = action.payload;
  if (!state.chatMessages[chatID]) {
    state.chatMessages[chatID] = getInitialChatState();
  }
  for (const message of messages) {
    if (!state.chatMessages[chatID].map[message.id]) {
      state.chatMessages[chatID].items.push(message.id);
    }
    state.chatMessages[chatID].map[message.id] = message;
  }
  const itemsForSort = state.chatMessages[chatID].items.map((id) => {
    const { createdAt } = state.chatMessages[chatID].map[id];
    return { id, ts: new Date(createdAt).getTime() };
  });
  state.chatMessages[chatID].items = itemsForSort
    .sort((a, b) => b.ts - a.ts)
    .map((i) => i.id);

  // tool_call
  const messagesForToolCallHandler = state.chatMessages[chatID].items
    .map((id) => state.chatMessages[chatID].map[id])
    .reverse();

  for (const message of messagesForToolCallHandler) {
    // tool_call: handle call
    if (
      message.source === MessageSource.Llm &&
      message.content.role === 'assistant'
    ) {
      const { toolCalls } = message.content;
      if (!toolCalls) {
        continue;
      }
      toolCalls.forEach((tool) => {
        if (!tool.id) {
          return;
        }
        state.chatMessages[chatID].toolCallMap[tool.id] = {
          name: tool.function.name,
          arguments: tool.function.arguments,
          loading: true,
        };
      });
    }

    // tool_call: handle result
    if (
      message.source === MessageSource.Tool &&
      message.content.role === 'tool'
    ) {
      const { content, toolCallId } = message.content;
      if (!state.chatMessages[chatID].toolCallMap[toolCallId]) {
        continue;
      }
      state.chatMessages[chatID].toolCallMap[toolCallId] = {
        ...state.chatMessages[chatID].toolCallMap[toolCallId],
        result: content,
        loading: false,
      };
    }
  }

  state.chatMessages[chatID].loading = false;
};

export type StreamingMessage = {
  text: string;
  message: TMessage;
};

export type MessageState = {
  chatMessages: Dictionary<ChatMessages>;
  streamingMessages: Dictionary<StreamingMessage>;
};

export type ChatMessages = {
  map: Dictionary<TMessage>;
  toolCallMap: Dictionary<ToolCall>;
  items: string[];
  page: number;
  loading: boolean;
  isEnd: boolean;

  isAssistantResponding: boolean;
  currentToolCalls: ToolCall[];

  activeUserMessage?: string;
  activeAssistantMessage?: string;
};

export type ToolCall = {
  name: string;
  loading: boolean;
  arguments?: any;
  result?: any;
};

const initialState: MessageState = {
  chatMessages: {},
  streamingMessages: {},
};

export const messageSlice = createSlice({
  name: 'message',
  initialState,
  reducers: {
    addMessages,

    setLoading: (
      state,
      action: PayloadAction<{ chatID: string; loading: boolean }>,
    ) => {
      const { chatID, loading } = action.payload;
      if (!state.chatMessages[chatID]) {
        state.chatMessages[chatID] = getInitialChatState();
      }
      state.chatMessages[chatID].loading = loading;
    },

    setPage: (
      state,
      action: PayloadAction<{ chatID: string; page: number }>,
    ) => {
      const { chatID, page } = action.payload;
      if (!state.chatMessages[chatID]) {
        state.chatMessages[chatID] = getInitialChatState();
      }
      state.chatMessages[chatID].page = page;
    },

    setIsEnd: (
      state,
      action: PayloadAction<{ chatID: string; isEnd: boolean }>,
    ) => {
      const { chatID, isEnd } = action.payload;
      if (!state.chatMessages[chatID]) {
        state.chatMessages[chatID] = getInitialChatState();
      }
      state.chatMessages[chatID].isEnd = isEnd;
    },

    setAssistantResponding: (
      state,
      action: PayloadAction<{ chatID: string; isResponding: boolean }>,
    ) => {
      const { chatID, isResponding } = action.payload;
      if (!state.chatMessages[chatID]) {
        state.chatMessages[chatID] = getInitialChatState();
      }
      state.chatMessages[chatID].isAssistantResponding = isResponding;
    },

    resetActive: (state, action: PayloadAction<{ chatID: string }>) => {
      const { chatID } = action.payload;
      if (!state.chatMessages[chatID]) {
        state.chatMessages[chatID] = getInitialChatState();
      }
      delete state.chatMessages[chatID].activeUserMessage;
      delete state.chatMessages[chatID].activeAssistantMessage;
      state.chatMessages[chatID].isAssistantResponding = false;
    },

    addActiveUserMessage: (
      state,
      action: PayloadAction<{ chatID: string; text: string }>,
    ) => {
      const { chatID, text } = action.payload;
      if (!state.chatMessages[chatID]) {
        state.chatMessages[chatID] = getInitialChatState();
      }
      state.chatMessages[chatID].activeUserMessage = text;
      state.chatMessages[chatID].isAssistantResponding = true;
    },

    clearActiveUserMessage: (
      state,
      action: PayloadAction<{ chatID: string }>,
    ) => {
      const { chatID } = action.payload;
      if (state.chatMessages[chatID]) {
        delete state.chatMessages[chatID].activeUserMessage;
      }
    },

    addChunkOfActiveAssistantMessage: (
      state,
      action: PayloadAction<{ chatID: string; chunk: string }>,
    ) => {
      const { chatID, chunk } = action.payload;
      if (!state.chatMessages[chatID]) {
        state.chatMessages[chatID] = getInitialChatState();
      }
      if (!state.chatMessages[chatID].activeAssistantMessage) {
        state.chatMessages[chatID].activeAssistantMessage = '';
      }
      state.chatMessages[chatID].activeAssistantMessage += chunk;
      state.chatMessages[chatID].isAssistantResponding = true;
    },
    setToolCall: (
      state,
      action: PayloadAction<{
        chatID: string;
        name: string;
        loading: boolean;
        arguments?: any;
        result?: any;
      }>,
    ) => {
      const { chatID, name, loading, arguments: args, result } = action.payload;
      if (!state.chatMessages[chatID]) {
        state.chatMessages[chatID] = getInitialChatState();
      }

      const chatState = state.chatMessages[chatID];

      const existingToolIndex = chatState.currentToolCalls.findIndex(
        (tool) => tool.name === name,
      );

      if (existingToolIndex === -1) {
        chatState.currentToolCalls.push({
          name,
          loading,
          arguments: args,
          result,
        });
      } else {
        chatState.currentToolCalls[existingToolIndex] = {
          ...chatState.currentToolCalls[existingToolIndex],
          loading,
          arguments:
            args || chatState.currentToolCalls[existingToolIndex].arguments,
          result:
            result || chatState.currentToolCalls[existingToolIndex].result,
        };
      }

      if (result) {
        const messages = Object.values(chatState.map);
        for (const message of messages) {
          if (message.content.role !== 'assistant') {
            continue;
          }
          const toolCall = message.content.toolCalls?.find(
            (tool) => tool.function.name.split('_')[0] === name,
          );

          if (toolCall) {
            message.content.toolCalls = message.content.toolCalls?.map(
              (tool) => {
                if (tool.function.name.split('_')[0] === name) {
                  return {
                    ...tool,
                    result,
                  };
                }
                return tool;
              },
            );
            break;
          }
        }
      }
    },

    initStreaming: (
      state,
      action: PayloadAction<{ chatID: string; message: TMessage }>,
    ) => {
      const { chatID, message } = action.payload;
      console.log('[Streaming] init', { chatID, messageId: message.id });
      state.streamingMessages[chatID] = {
        text: '',
        message,
      };
    },

    appendStreamingChunk: (
      state,
      action: PayloadAction<{ chatID: string; chunk: string }>,
    ) => {
      const { chatID, chunk } = action.payload;
      const existing = state.streamingMessages[chatID];
      console.log('[Streaming] appendChunk', {
        chatID,
        chunkLength: chunk?.length,
        hasStreamingEntry: !!existing,
        currentTextLength: existing?.text?.length,
      });
      if (existing) {
        existing.text += chunk;
      }
    },

    completeStreaming: (state, action: PayloadAction<{ chatID: string }>) => {
      const { chatID } = action.payload;
      const streaming = state.streamingMessages[chatID];
      console.log('[Streaming] complete', {
        chatID,
        hasStreamingEntry: !!streaming,
        finalTextLength: streaming?.text?.length,
      });
      if (streaming) {
        const finalMessage: TMessage = {
          ...streaming.message,
          content: {
            role: 'assistant' as const,
            content: streaming.text,
          },
        };

        if (!state.chatMessages[chatID]) {
          state.chatMessages[chatID] = getInitialChatState();
        }
        const chat = state.chatMessages[chatID];
        if (!chat.map[finalMessage.id]) {
          chat.items.push(finalMessage.id);
        }
        chat.map[finalMessage.id] = finalMessage;

        const itemsForSort = chat.items.map((id) => {
          const { createdAt } = chat.map[id];
          return { id, ts: new Date(createdAt).getTime() };
        });
        chat.items = itemsForSort.sort((a, b) => b.ts - a.ts).map((i) => i.id);

        delete state.streamingMessages[chatID];
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(getAll.fulfilled, addMessages);
  },
});

export const isAssistantRespondingSelector = (
  state: RootState,
  chatID: string,
) => state.message.chatMessages[chatID]?.isAssistantResponding ?? false;

export const {
  actions: messageActions,
  reducer: messageReducer,
  selectors: messageSelectors,
} = messageSlice;

export const messageThunks = {
  getAll,
};
